// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    loaded: !!getProductListDataView(state).value,
    productFilters: state.pages.productList.productFilters,
    hideSearchWithin: getSettingsCollection(state).searchSettings.searchProvider === "Commerce Search v3",
});

const mapDispatchToProps = {
    addProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListSearchWithinStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    searchTextField?: TextFieldPresentationProps;
}

export const searchWithinStyles: ProductListSearchWithinStyles = {
    titleText: {
        variant: "p",
        weight: fontConfig.weight.bold,
        transform: "uppercase",
        css: css`
            letter-spacing: 0.23px;
        `,
    },
    searchTextField: {
        iconProps: {
            src: "Search",
        },
        cssOverrides: {
            formField: css`
                width: 100%;
                > div {
                    margin-top: 0 !important;

                    button {
                        position: absolute;
                        left: 0;
                        span {
                            padding: 0 !important;
                            right: auto !important;
                            top: auto !important;
                        }
                        svg {
                            color: ${getColor("secondary")};
                            width: 20px;
                            height: 20px;
                        }
                    }
                    input {
                        padding-left: 40px !important;
                    }
                }
            `,
        },
    },
    wrapper: {
        css: css`
            margin-bottom: 30px;
            padding-top: 17px;
            border-top: 2px solid ${getColor("common.ui3")};
        `,
    },
};

const styles = searchWithinStyles;

const ProductListSearchWithin: FC<Props> = ({ loaded, addProductFilters, productFilters, hideSearchWithin  }) => {
    if (!loaded) {
        return null;
    }

    const [searchText, setSearchText] = useState("");

    const doSearch = () => {
        if (searchText.trim() !== "") {
            addProductFilters({ searchWithinQueries: [searchText] });
        }
        setSearchText("");
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            doSearch();
        } else {
            setSearchText(event.currentTarget.value);
        }
    };

    let pageType: string;
    if (productFilters.pageProductLineId) {
        pageType = "product line";
    } else if (productFilters.pageBrandId) {
        pageType = "brand";
    } else if (productFilters.pageCategoryId) {
        pageType = "category";
    } else {
        pageType = "results";
    }

    const label = translate(`Search within ${pageType}`);

    if (hideSearchWithin) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography htmlFor="productsKeywordSearch" {...styles.titleText}>
                {label}
            </Typography>
            <TextField
                aria-label={translate(`Product List Search box`)}
                {...styles.searchTextField}
                iconClickableProps={{ onClick: doSearch, "aria-label": `Search within ${pageType}` }}
                value={searchText}
                onChange={event => setSearchText(event.currentTarget.value)}
                onKeyDown={handleKeyDown}
                placeholder={translate("Search")}
                type="text"
                data-test-selector="productListSearchWithin"
                id="productsKeywordSearch"
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListSearchWithin),
    definition: {
        group: "Product List",
        displayName: "Search Within",
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
